import { createContext, Deps, ReadOnlyRef, useEffect, useMemo, useRef } from "@lukekaalim/act"
import { Observable, Subject } from "rxjs";
import { subscribeEvent } from "./events";
import { createJanitor } from "./janitor";

export type KeyboardInput = {
  /** @mutable */
  heldKeyCodes: Set<string>,
  /** @mutable */
  pressedKeyCodes: Set<string>,
  /** @mutable */
  releasedKeyCodes: Set<string>,

  onHeldKeysChange: Observable<KeyboardInput>,
  onKeyPress: Observable<KeyboardEvent>
}


export const useKeyboardInput = (
  ref: ReadOnlyRef<HTMLElement | null>,
  deps: Deps = [],
  enabled: boolean = true
): [input: KeyboardInput, onClearInputFrame: () => void] => {
  const keyboardInput = useRef(() => ({
    heldKeyCodes: new Set<string>(),
    pressedKeyCodes: new Set<string>(),
    releasedKeyCodes: new Set<string>(),

    onHeldKeysChange: new Subject<KeyboardInput>(),
    onKeyPress: new Subject<KeyboardEvent>()
  })).get();

  useEffect(() => {
    const element = ref.get();
    if (!element || !enabled)
      return;

    const j = createJanitor();

    j.add(subscribeEvent(element, 'keydown', (event) => {
      keyboardInput.onKeyPress.next(event);
      if (event.repeat)
        return;
      keyboardInput.heldKeyCodes.add(event.code)
      keyboardInput.pressedKeyCodes.add(event.code);

      keyboardInput.onHeldKeysChange.next(keyboardInput);
    }))
    j.add(subscribeEvent(element, 'keyup', (event) => {
      if (keyboardInput.heldKeyCodes.delete(event.code)) {
        keyboardInput.releasedKeyCodes.add(event.code)
      }

      keyboardInput.onHeldKeysChange.next(keyboardInput);
    }))
    j.add(subscribeEvent(element, 'contextmenu', (e) => {
      if (e.defaultPrevented)
        return;
      keyboardInput.heldKeyCodes.clear();
      keyboardInput.onHeldKeysChange.next(keyboardInput);
    }))
    j.add(subscribeEvent(element, 'focusout', (event) => {
      if (event.relatedTarget instanceof Node && element.contains(event.relatedTarget)) {
        // don' clear if we're just refocusing on a child or something
        return;
      }
      if (event.relatedTarget === null) {
        // If we're switching to focus on nothing at all, why not focus on us?
        // (this may be a bad idea)
        element.focus()
        return
      }

      keyboardInput.heldKeyCodes.clear();
      keyboardInput.onHeldKeysChange.next(keyboardInput);
    }))

    return j.cleanup;
  }, deps);

  const onClearInputFrame = useMemo(() => () => {
    keyboardInput.pressedKeyCodes.clear();
    keyboardInput.releasedKeyCodes.clear();
  }, [])

  return [keyboardInput, onClearInputFrame];
}
